import { useCallback, useEffect, useRef, useState } from 'react';
import type { FormEvent } from 'react';
import { sendBotImprovement, sendClientImprovement } from '../../../../services/api.js';

type ImprovementKind = 'bot' | 'client';

type ImprovementMessage = {
  content?: string | null;
  [key: string]: unknown;
};

type UseMessageImprovementsOptions = {
  /** Troca de conversa: fecha modal e limpa texto. */
  resetKey?: string | number | null;
};

export function useMessageImprovements({ resetKey }: UseMessageImprovementsOptions = {}) {
  const [isImprovementOpen, setIsImprovementOpen] = useState(false);
  const [improvementMessage, setImprovementMessage] = useState<ImprovementMessage | null>(null);
  const [improvementText, setImprovementText] = useState('');
  const [improvementError, setImprovementError] = useState<string | null>(null);
  const [improvementSuccess, setImprovementSuccess] = useState<string | null>(null);
  const [isImprovementSending, setIsImprovementSending] = useState(false);
  const [improvementKind, setImprovementKind] = useState<ImprovementKind | null>(null);
  const closeTimeoutRef = useRef<number | null>(null);

  const clearCloseTimeout = useCallback(() => {
    if (closeTimeoutRef.current != null) {
      window.clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  }, []);

  const handleCloseImprovement = useCallback(() => {
    clearCloseTimeout();
    setIsImprovementOpen(false);
    setImprovementMessage(null);
    setImprovementText('');
    setImprovementError(null);
    setImprovementSuccess(null);
    setImprovementKind(null);
  }, [clearCloseTimeout]);

  const handleOpenImprovement = useCallback((message: ImprovementMessage, kind: ImprovementKind) => {
    if (!message?.content) return;
    clearCloseTimeout();
    setImprovementMessage(message);
    setImprovementText('');
    setImprovementError(null);
    setImprovementSuccess(null);
    setImprovementKind(kind);
    setIsImprovementOpen(true);
  }, [clearCloseTimeout]);

  const handleOpenBotImprovement = useCallback((message: ImprovementMessage) => {
    handleOpenImprovement(message, 'bot');
  }, [handleOpenImprovement]);

  const handleOpenClientImprovement = useCallback((message: ImprovementMessage) => {
    handleOpenImprovement(message, 'client');
  }, [handleOpenImprovement]);

  useEffect(() => {
    if (!isImprovementOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        handleCloseImprovement();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isImprovementOpen, handleCloseImprovement]);

  useEffect(() => {
    handleCloseImprovement();
  }, [resetKey]); // eslint-disable-line react-hooks/exhaustive-deps -- reset só quando a conversa muda

  useEffect(() => () => clearCloseTimeout(), [clearCloseTimeout]);

  const handleSubmitImprovement = async (event: FormEvent) => {
    event.preventDefault();
    if (!improvementMessage?.content || !improvementText.trim() || !improvementKind) return;
    setIsImprovementSending(true);
    setImprovementError(null);
    try {
      if (improvementKind === 'bot') {
        await sendBotImprovement({
          botMessage: improvementMessage.content,
          correction: improvementText.trim(),
        });
        setImprovementSuccess('Sugestão enviada! Vamos aplicar essa melhoria na IA.');
      } else {
        await sendClientImprovement({
          clientMessage: improvementMessage.content,
          answer: improvementText.trim(),
        });
        setImprovementSuccess('Sugestão enviada! A IA vai aprender a responder melhor.');
      }
      setImprovementText('');
      clearCloseTimeout();
      closeTimeoutRef.current = window.setTimeout(() => {
        handleCloseImprovement();
      }, 1200);
    } catch (err: any) {
      const message = err?.response?.data?.error || err?.message || 'Erro ao enviar correção.';
      setImprovementError(message);
    }
    setIsImprovementSending(false);
  };

  return {
    isImprovementOpen,
    improvementMessage,
    improvementText,
    setImprovementText,
    improvementError,
    improvementSuccess,
    isImprovementSending,
    improvementKind,
    handleOpenImprovement,
    handleOpenBotImprovement,
    handleOpenClientImprovement,
    handleCloseImprovement,
    handleSubmitImprovement,
  };
}
